import {
  type LanguageCode,
  withLanguagePath,
} from '@/lib/url-language-utils'
import { resolveAvailableContentLocale } from '../../functions/public-content-locale.js'

export type BlogContentLanguage = 'en' | 'zh'

export interface BlogLocaleConfig {
  basePath: string
}

export interface BlogSource {
  id: string
  defaultLocale: BlogContentLanguage
  locales: Partial<Record<BlogContentLanguage, BlogLocaleConfig>>
}

export interface ResolvedBlogSource {
  id: string
  locale: BlogContentLanguage
  basePath: string
  path: string
}

export interface BlogMetadata {
  title: string
  description: string
  publishedAt: string
  coverImage: string
}

export function resolveBlogSource(
  source: BlogSource,
  preferredLanguage: LanguageCode,
): ResolvedBlogSource | null {
  const locale = resolveAvailableContentLocale(
    preferredLanguage,
    Object.keys(source.locales) as BlogContentLanguage[],
  ) ?? source.defaultLocale
  const config = source.locales[locale]
  if (!config)
    return null

  return {
    id: source.id,
    locale,
    basePath: config.basePath,
    path: blogArticlePath(source.id, locale),
  }
}

export function blogArticlePath(id: string, locale: BlogContentLanguage): string {
  return withLanguagePath(`/blogs/${id}`, locale)
}

export function blogCanonicalPath(id: string, requestedLanguage: LanguageCode): string | null {
  const source = blogSources.find(entry => entry.id === id)
  return source ? resolveBlogSource(source, requestedLanguage)?.path ?? null : null
}

export function blogListPath(language: LanguageCode): string {
  return withLanguagePath('/blogs', language)
}

export const blogSources: BlogSource[] = [
  {
    id: 'mentiss-rlvr',
    defaultLocale: 'en',
    locales: {
      en: {
        basePath: '/blogs/en/mentiss-rlvr',
      },
      zh: {
        basePath: '/blogs/zh/mentiss-rlvr',
      },
    },
  },
  {
    id: 'leaderboard-challenger',
    defaultLocale: 'en',
    locales: {
      en: {
        basePath: '/blogs/en/leaderboard-challenger',
      },
      zh: {
        basePath: '/blogs/zh/leaderboard-challenger',
      },
    },
  },
  {
    id: 'google-game-arena-werewolf',
    defaultLocale: 'en',
    locales: {
      en: {
        basePath: '/blogs/en/google-game-arena-werewolf',
      },
    },
  },
  {
    id: 'why-werewolf',
    defaultLocale: 'zh',
    locales: {
      zh: {
        basePath: '/blogs/zh/why-werewolf',
      },
      en: {
        basePath: '/blogs/en/why-werewolf',
      },
    },
  },
  {
    id: 'four-vision',
    defaultLocale: 'zh',
    locales: {
      zh: {
        basePath: '/blogs/zh/four-vision',
      },
      en: {
        basePath: '/blogs/en/four-vision',
      },
    },
  },
  {
    id: 'model-autophagy-disorder',
    defaultLocale: 'zh',
    locales: {
      zh: {
        basePath: '/blogs/zh/model-autophagy-disorder',
      },
      en: {
        basePath: '/blogs/en/model-autophagy-disorder',
      },
    },
  },
  {
    id: 'bring-your-model',
    defaultLocale: 'en',
    locales: {
      en: {
        basePath: '/blogs/en/bring-your-model',
      },
      zh: {
        basePath: '/blogs/zh/bring-your-model',
      },
    },
  },
  {
    id: 'benchmark-report',
    defaultLocale: 'en',
    locales: {
      en: {
        basePath: '/blogs/en/benchmark-report',
      },
      zh: {
        basePath: '/blogs/zh/benchmark-report',
      },
    },
  },
]
